/**
 * checkoutLock.js
 * ---------------
 * Client-side idempotency guard for the Stripe checkout return flow.
 *
 * Prevents the applyCheckoutCredits() function in SubscriptionContext from
 * calling /api/v1/payments/verify-checkout-session more than once for the
 * same Stripe session_id — even across browser tabs or React re-renders.
 *
 * Mechanism:
 *  - "Lock"      → sessionStorage key `checkout_lock_<sessionId>` (per-tab, cleared on close)
 *  - "Done mark" → localStorage key  `checkout_done_<sessionId>` (persists across tabs/reloads)
 *
 * The "done" flag lives in localStorage so a user refreshing after a successful
 * checkout doesn't trigger a second credit call.
 * The lock key uses sessionStorage so it is automatically released if the tab crashes.
 *
 * Usage:
 *   const { acquired, lockKey } = tryAcquireCheckoutLock(sessionId);
 *   if (!acquired) return; // already done or another call is in flight
 *   try {
 *     await applyCredits();
 *     markCheckoutLockDone(lockKey);
 *   } catch (err) {
 *     resetCheckoutLockForRetry(lockKey); // allow retry
 *   }
 */

const LOCK_PREFIX = 'checkout_lock_';
const DONE_PREFIX = 'checkout_done_';
const LOCK_TTL_MS = 10 * 60 * 1000; // 10 min — safety net for crashed tabs

/**
 * Attempt to acquire an exclusive checkout lock for the given session.
 *
 * @param {string} sessionId  Stripe checkout session ID (e.g. "cs_test_...")
 * @returns {{ acquired: boolean, lockKey: string }}
 *   acquired  - true if the caller may proceed with credit application
 *   lockKey   - opaque key to pass to markCheckoutLockDone / resetCheckoutLockForRetry
 */
export function tryAcquireCheckoutLock(sessionId) {
  if (!sessionId) {
    return { acquired: false, lockKey: null };
  }

  const lockKey = LOCK_PREFIX + sessionId;
  const doneKey = DONE_PREFIX + sessionId;

  // 1. Already permanently done? (localStorage persists across reloads)
  try {
    if (localStorage.getItem(doneKey)) {
      return { acquired: false, lockKey };
    }
  } catch (_) {
    // localStorage unavailable — continue; worst case we double-call but server is idempotent
  }

  // 2. Another tab/call currently holds the lock?
  try {
    const existing = sessionStorage.getItem(lockKey);
    if (existing) {
      const lockData = JSON.parse(existing);
      const age = Date.now() - (lockData.ts || 0);
      if (age < LOCK_TTL_MS) {
        // Lock is fresh — don't acquire
        return { acquired: false, lockKey };
      }
      // Stale lock (crashed tab?) — remove and re-acquire
      sessionStorage.removeItem(lockKey);
    }
  } catch (_) {
    // sessionStorage unavailable — fall through, server idempotency is the safety net
  }

  // 3. Acquire the lock
  try {
    sessionStorage.setItem(lockKey, JSON.stringify({ ts: Date.now() }));
  } catch (_) {}

  return { acquired: true, lockKey };
}

/**
 * Permanently mark the checkout as successfully processed.
 * Releases the in-flight lock and sets the persistent "done" flag.
 *
 * @param {string} lockKey  Value returned from tryAcquireCheckoutLock
 */
export function markCheckoutLockDone(lockKey) {
  if (!lockKey) return;

  // Derive the sessionId from the lockKey
  const sessionId = lockKey.replace(LOCK_PREFIX, '');
  const doneKey = DONE_PREFIX + sessionId;

  try {
    localStorage.setItem(doneKey, '1');
  } catch (_) {}

  try {
    sessionStorage.removeItem(lockKey);
  } catch (_) {}
}

/**
 * Release the in-flight lock WITHOUT marking as done.
 * Call this after a failed attempt so the next retry can re-acquire.
 *
 * @param {string} lockKey  Value returned from tryAcquireCheckoutLock
 */
export function resetCheckoutLockForRetry(lockKey) {
  if (!lockKey) return;
  try {
    sessionStorage.removeItem(lockKey);
  } catch (_) {}
}

/**
 * Clear both the lock and the done flag for a session.
 * Only needed in testing or manual recovery scenarios.
 *
 * @param {string} sessionId  Stripe checkout session ID
 */
export function clearCheckoutLock(sessionId) {
  if (!sessionId) return;
  const lockKey = LOCK_PREFIX + sessionId;
  const doneKey = DONE_PREFIX + sessionId;
  try { sessionStorage.removeItem(lockKey); } catch (_) {}
  try { localStorage.removeItem(doneKey); } catch (_) {}
}

/** True after a successful verify for this checkout id (persists across reloads). */
export function isCheckoutAlreadyDone(checkoutId) {
  if (!checkoutId) return false;
  try {
    return Boolean(localStorage.getItem(DONE_PREFIX + checkoutId));
  } catch (_) {
    return false;
  }
}

const INFLIGHT_PREFIX = 'checkout_inflight_';

/** Prevent parallel verify calls from Success page + Dashboard + context init. */
export function beginCheckoutVerifyInflight(checkoutId) {
  if (!checkoutId) return false;
  const key = INFLIGHT_PREFIX + checkoutId;
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch (_) {
    return true;
  }
}

export function endCheckoutVerifyInflight(checkoutId) {
  if (!checkoutId) return;
  try {
    sessionStorage.removeItem(INFLIGHT_PREFIX + checkoutId);
  } catch (_) {}
}
